import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Drawer, IconButton, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Row from "../../components/customComponents/layout/row/Row";
import styles from "./sideNav.module.scss";
import logoWhite from "../../assets/logo/cmpLogo.png";
import { modifyPanel } from "../../store/actions";

const findActiveTabByPathname = (item = {}, path = "") => {
  if (!item?.link) return false;

  const matchStrategy = item.match || "segment";

  if (matchStrategy === "exact") {
    return path === item.link;
  }

  if (matchStrategy === "startsWith") {
    return path === item.link || path.startsWith(`${item.link}/`);
  }

  return path.includes(item.link);
};

const SideNav = ({
  navItems = [],
  mobileOpen = false,
  onClose = () => {},
  triggerLogout,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const panel = useSelector((state) => state?.ui?.panel);
  const [expandedItem, setExpandedItem] = useState(null);
  const theme = useTheme();
  const loginUser = useSelector((state) => state?.login?.user);
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const isCollapsed = isDesktop && panel?.show === false;
  const drawerWidth = isCollapsed ? 135 : 280;
  const shouldCloseOnSelection = !isDesktop;

  const enhancedItems = useMemo(
    () =>
      navItems.map((item) => ({
        ...item,
        children: item?.children || [],
        badge: item?.badge,
        divider: item?.divider,
        subtitle: item?.subtitle,
        match: item?.match,
      })),
    [navItems]
  );

  useEffect(() => {
    if (isCollapsed) {
      setExpandedItem(null);
    }
  }, [isCollapsed]);

  useEffect(() => {
    if (isCollapsed) return;

    const activeParent = enhancedItems.find((item) =>
      item.children.some((child) => pathname.includes(child.link || ""))
    );

    if (activeParent?.id && activeParent.id !== expandedItem) {
      setExpandedItem(activeParent.id);
    }
  }, [enhancedItems, pathname, isCollapsed]);

  const handleItemClick = (item, hasChildren, isActiveItem) => {
    if (hasChildren) {
      setExpandedItem((prev) => (prev === item.id ? null : item.id));
      return;
    }
    if (!isActiveItem && item?.link) {
      navigate(item.link);
      if (shouldCloseOnSelection) {
        onClose();
      }
    }
  };

  function stringToColor(string) {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string?.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */

    return color;
  }

  function stringAvatar(name) {
    return {
      sx: {
        bgcolor: stringToColor(name),
      },
      children: `${name?.split(" ")[0][0]}`,
    };
  }

  console.log("loginUser", loginUser);

  const renderChildren = (item) => {
    if (!item.children?.length || expandedItem !== item.id) return null;

    return (
      <div className={styles.subMenu}>
        {item.children.map((child) => {
          const childIsActive = pathname.includes(child.link || "");

          return (
            <button
              type="button"
              key={child.id || child.link}
              className={`${styles.subMenuItem} ${
                childIsActive ? styles.subMenuItemActive : ""
              }`}
              onClick={() => {
                if (!child?.link) return;
                navigate(child.link);
                if (shouldCloseOnSelection) {
                  onClose();
                }
              }}
            >
              <span className={styles.subMenuIndicator} />
              <span className={styles.subMenuTitle}>{child.title}</span>
              {child?.badge && (
                <span className={styles.badge}>{child.badge}</span>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <Drawer
      variant={isDesktop ? "permanent" : "temporary"}
      open={isDesktop ? true : mobileOpen}
      onClose={isDesktop ? undefined : onClose}
      ModalProps={
        isDesktop
          ? undefined
          : {
              keepMounted: true,
            }
      }
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          borderRight: "none",
          backgroundColor: "transparent",
          boxShadow: "none",
          overflow: "visible",
          padding: 0,
        },
      }}
    >
      <div className={isCollapsed ? styles.closed : styles.container}>
        <div className={styles.brandCardWrapper}>
          <div className={styles.brandCard}>
            <div className={styles.brandLogo}>
              <img src={logoWhite} alt="Company logo" />
            </div>
            {!isCollapsed && (
              <div className={styles.brandMeta}>
                <span className={styles.brandTitle}>Kriha Commerce</span>
                {/* <span className={styles.brandSubtitle}>by CollabOn</span> */}
              </div>
            )}
            {!isCollapsed && isDesktop && (
              <IconButton
                onClick={() => dispatch(modifyPanel({ show: false }))}
                classes={{ root: styles.collapseAction }}
              >
                <span className="material-symbols-outlined">
                  keyboard_double_arrow_left
                </span>
              </IconButton>
            )}
          </div>
          {isCollapsed && (
            <IconButton
              onClick={() => dispatch(modifyPanel({ show: true }))}
              classes={{ root: styles.collapseActionSmall }}
            >
              <span class="material-symbols-outlined">double_arrow</span>
            </IconButton>
          )}
        </div>

        <nav className={styles.menuWrapper}>
          {enhancedItems.map((item) => {
            const hasChildren = item.children?.length > 0;
            const isActive =
              findActiveTabByPathname(item, pathname) ||
              item.children.some((child) =>
                pathname.includes(child.link || "")
              );
            const isExpanded = expandedItem === item.id;

            return (
              <React.Fragment key={item.id || item.title}>
                <button
                  type="button"
                  className={`${styles.menuItem} ${
                    isActive ? styles.menuItemActive : ""
                  } ${isCollapsed ? styles.menuItemCollapsed : ""}`}
                  onClick={() =>
                    handleItemClick(item, hasChildren, isActive && !hasChildren)
                  }
                >
                  <span
                    className={`${styles.iconButton} material-symbols-outlined`}
                  >
                    {item.icon}
                  </span>
                  {!isCollapsed && (
                    <div className={styles.menuCopy}>
                      <span className={styles.menuTitle}>{item.title}</span>
                    </div>
                  )}
                  {!isCollapsed && item?.badge && (
                    <span className={styles.badge}>{item.badge}</span>
                  )}
                  {!isCollapsed && hasChildren && (
                    <span
                      className={`${styles.expandIcon} material-symbols-outlined`}
                    >
                      {isExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                    </span>
                  )}
                </button>
                {renderChildren(item)}
                {item.divider && <div className={styles.divider} />}
              </React.Fragment>
            );
          })}
        </nav>

        {isCollapsed ? (
          <Row className={styles.seeLessContainer}>
            {loginUser?.imageUrl ? (
              <img src={loginUser?.imageUrl} alt="" width={40} height={40} />
            ) : (
              <Avatar
                variant="rounded"
                {...stringAvatar(loginUser?.fullName)}
              />
            )}
          </Row>
        ) : (
          <Row className={styles.footerCard}>
            <div className={styles.footerProfile}>
              {/* <div className={styles.avatarPlaceholder}>NK</div> */}

              {loginUser?.imageUrl ? (
                <img src={loginUser?.imageUrl} alt="" width={40} height={40} />
              ) : (
                <Avatar
                  variant="rounded"
                  {...stringAvatar(loginUser?.fullName)}
                />
              )}

              <div className={styles.profileMeta}>
                <span className={styles.profileName}>
                  {loginUser?.fullName}
                </span>
                <span className={styles.profileRole}>{loginUser?.role}</span>
              </div>
            </div>
            <IconButton
              classes={{ root: styles.profileAction }}
              onClick={() => triggerLogout()}
            >
              <span className="material-symbols-outlined">logout</span>
            </IconButton>
          </Row>
        )}
      </div>
    </Drawer>
  );
};

export default SideNav;
